import { useEffect, useState, useCallback } from 'react';
import { Search, Ban, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Table } from '@/components/ui/Table';
import { Avatar } from '@/components/ui/Avatar';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import api from '@/lib/api';
import { Role, UserStatus } from '@hirenfire/shared';
import type { ApiResponse, UserWithProfile } from '@hirenfire/shared';

type BadgeVariant = 'success' | 'warning' | 'error' | 'default' | 'primary' | 'purple';

const statusVariant: Record<UserStatus, BadgeVariant> = {
  [UserStatus.ACTIVE]: 'success',
  [UserStatus.SUSPENDED]: 'warning',
  [UserStatus.BANNED]: 'error',
  [UserStatus.DEACTIVATED]: 'default',
};

export function AdminUsersPage() {
  const [users, setUsers] = useState<UserWithProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithProfile | null>(null);
  const [banReason, setBanReason] = useState('');

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      const res = await api.get<ApiResponse<UserWithProfile[]>>(`/admin/users?${params}`);
      if (res.data.data) setUsers(res.data.data);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleBan = async () => {
    if (!selectedUser) return;
    try {
      await api.post(`/admin/users/${selectedUser.id}/ban`, { reason: banReason });
      setShowBanModal(false);
      setBanReason('');
      setSelectedUser(null);
      fetchUsers();
    } catch {
      // handle error
    }
  };

  const handleUnban = async (userId: string) => {
    try {
      await api.post(`/admin/users/${userId}/unban`);
      fetchUsers();
    } catch {
      // handle error
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">User Management</h1>
        <p className="text-gray-400 mt-1">Manage platform users</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          options={[
            { value: '', label: 'All Roles' },
            ...Object.values(Role).map((r) => ({ value: r, label: r })),
          ]}
        />
      </div>

      <Card>
        <Table
          isLoading={isLoading}
          keyExtractor={(u) => u.id}
          emptyMessage="No users found"
          columns={[
            {
              key: 'user',
              header: 'User',
              render: (u) => (
                <div className="flex items-center gap-3">
                  <Avatar
                    src={u.profile?.avatar}
                    name={u.profile?.displayName ?? u.email}
                    size="sm"
                  />
                  <div>
                    <p className="text-white font-medium">
                      {u.profile?.displayName ?? 'No name'}
                    </p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </div>
                </div>
              ),
            },
            {
              key: 'role',
              header: 'Role',
              render: (u) => (
                <Badge variant={u.role === Role.ADMIN ? 'error' : u.role === Role.AGENCY ? 'purple' : 'primary'}>
                  {u.role}
                </Badge>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (u) => (
                <Badge variant={statusVariant[u.status]}>{u.status}</Badge>
              ),
            },
            {
              key: 'kyc',
              header: 'KYC',
              render: (u) => (
                <Badge variant={u.kyc?.status === 'APPROVED' ? 'success' : 'default'}>
                  {u.kyc?.status ?? 'N/A'}
                </Badge>
              ),
            },
            {
              key: 'joined',
              header: 'Joined',
              render: (u) => (
                <span className="text-gray-400">
                  {new Date(u.createdAt).toLocaleDateString()}
                </span>
              ),
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (u) => (
                <div className="flex items-center gap-2">
                  {u.status === UserStatus.BANNED ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnban(u.id);
                      }}
                      leftIcon={<CheckCircle className="w-3 h-3" />}
                    >
                      Unban
                    </Button>
                  ) : u.role !== Role.ADMIN ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUser(u);
                        setShowBanModal(true);
                      }}
                      leftIcon={<Ban className="w-3 h-3" />}
                    >
                      Ban
                    </Button>
                  ) : null}
                </div>
              ),
            },
          ]}
          data={users}
        />
      </Card>

      <Modal
        isOpen={showBanModal}
        onClose={() => {
          setShowBanModal(false);
          setSelectedUser(null);
          setBanReason('');
        }}
        title="Ban User"
      >
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">
            Are you sure you want to ban{' '}
            <span className="text-white font-medium">
              {selectedUser?.profile?.displayName ?? selectedUser?.email}
            </span>
            ?
          </p>
          <Textarea
            label="Reason"
            placeholder="Reason for banning..."
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            required
          />
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowBanModal(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleBan}>
              Confirm Ban
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
